import React, { useState, useEffect } from 'react';
import { 
  Alert, AlertTitle, Button, Snackbar, Box, 
  CircularProgress, Collapse, IconButton 
} from '@mui/material';
import { Close, Email, Refresh } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const EmailVerificationBanner = () => {
  const { 
    currentUser, 
    getEmailVerificationStatus, 
    resendVerificationEmail, 
    loading 
  } = useAuth();
  
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [resendStatus, setResendStatus] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  // Check if banner was dismissed in this session
  useEffect(() => {
    const dismissed = sessionStorage.getItem('emailVerificationBannerDismissed');
    setIsDismissed(dismissed === 'true');
  }, []);

  // Load verification status when user is logged in. currentUser (from
  // AuthContext's /auth/check-auth, via User.to_dict()) already carries
  // email_verified/needs_email_verification/can_resend_verification/email -
  // the same fields this used to fire its own /auth/verify-email-status
  // request just to re-fetch, on every authenticated page load (this banner
  // is mounted app-wide). Derive it from currentUser instead; the API is
  // still used after a resend (see handleResendEmail) since
  // can_resend_verification is rate-limited server-side and can genuinely
  // change as a result of that action.
  useEffect(() => {
    if (!currentUser || isDismissed) return;

    const status = {
      email_verified: currentUser.email_verified,
      needs_verification: currentUser.needs_email_verification,
      can_resend_verification: currentUser.can_resend_verification,
      email: currentUser.email
    };
    setVerificationStatus(status);

    // Show banner if user needs verification
    if (status.needs_verification) {
      setIsVisible(true);
    }
  }, [currentUser, isDismissed]);

  const handleResendEmail = async () => {
    if (!verificationStatus?.email) return;
    
    try {
      setIsLoading(true);
      const result = await resendVerificationEmail(verificationStatus.email);
      setResendStatus(result);
      setSnackbarOpen(true);
      
      // If successful, update the verification status
      if (result.success) {
        const updatedStatus = await getEmailVerificationStatus();
        setVerificationStatus(updatedStatus);
      }
    } catch (error) {
      setResendStatus({ 
        success: false, 
        message: 'Failed to resend verification email' 
      });
      setSnackbarOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDismiss = () => {
    setIsVisible(false);
    setIsDismissed(true);
    // Remember dismissal for this session
    sessionStorage.setItem('emailVerificationBannerDismissed', 'true');
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // Don't show if user is not logged in, already verified, or banner is dismissed
  if (!currentUser || !verificationStatus?.needs_verification || isDismissed) {
    return null;
  }

  return (
    <>
      <Collapse in={isVisible}>
        <Alert 
          severity="warning" 
          sx={{ 
            mb: 2,
            mx: 2,
            mt: 2,
            borderRadius: 2,
            '& .MuiAlert-action': {
              alignItems: 'flex-start',
              pt: 0.5
            }
          }}
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={handleDismiss}
            >
              <Close fontSize="inherit" />
            </IconButton>
          }
        >
          <AlertTitle sx={{ mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Email fontSize="small" />
              Email Verification Required
            </Box>
          </AlertTitle>
          
          <Box sx={{ mb: 2 }}>
            Please verify your email address to ensure account security and receive important notifications.
          </Box>
          
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            {verificationStatus?.can_resend_verification && (
              <Button 
                variant="contained" 
                size="small"
                startIcon={isLoading ? <CircularProgress size={16} /> : <Refresh />}
                onClick={handleResendEmail}
                disabled={isLoading}
                sx={{ 
                  bgcolor: 'warning.main',
                  color: 'warning.contrastText',
                  '&:hover': {
                    bgcolor: 'warning.dark'
                  }
                }}
              >
                {isLoading ? 'Sending...' : 'Send Verification Email'}
              </Button>
            )}
            
            {verificationStatus?.email && (
              <Box 
                component="span" 
                sx={{ 
                  fontSize: '0.875rem', 
                  color: 'text.secondary',
                  fontStyle: 'italic' 
                }}
              >
                Email: {verificationStatus.email}
              </Box>
            )}
          </Box>
        </Alert>
      </Collapse>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={resendStatus?.success ? 'success' : 'error'}
          sx={{ width: '100%' }}
        >
          {resendStatus?.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default EmailVerificationBanner;
